import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Product, Shop } from '@reims-local/types';
import { shopApi } from '../lib/api';
import { useCart } from '../lib/cart';
import { useCartUi } from '../components/CartDrawer';

export function ShopPage() {
  const { slug } = useParams<{ slug: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, cartActions] = useCart();
  const cartUi = useCartUi();

  useEffect(() => {
    if (!slug) return;
    shopApi
      .getPublic(slug)
      .then((res) => {
        setShop(res.data.shop);
        setProducts(res.data.products);
        setError(null);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [slug]);

  const categories = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const p of products) {
      const cat = p.category ?? 'Autres';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(p);
    }
    return Array.from(map.entries());
  }, [products]);

  if (loading) return <main className="container"><p>Chargement…</p></main>;
  if (error || !shop) return <main className="container"><div className="alert alert-error">{error ?? 'Boutique introuvable.'}</div></main>;

  function addToCart(p: Product) {
    if (!shop) return;
    const res = cartActions.addItem(
      { slug: shop.slug, name: shop.name },
      {
        product_id: p.id,
        name: p.name,
        price_cents: p.price_cents,
        photo_url: p.photo_url,
        category: p.category,
      },
    );
    if (!res.ok) {
      if (confirm(`Votre panier contient déjà des articles de ${res.existingShop}. Le vider pour continuer ?`)) {
        cartActions.clear();
        cartActions.addItem(
          { slug: shop.slug, name: shop.name },
          { product_id: p.id, name: p.name, price_cents: p.price_cents, photo_url: p.photo_url, category: p.category },
        );
        cartUi.open();
      }
    } else {
      cartUi.open();
    }
  }

  return (
    <main className="container">
      <div className="shop-hero">
        <div className="cover" style={{ backgroundImage: shop.cover_url ? `url(${shop.cover_url})` : undefined }} />
        <div className="info">
          <h2>{shop.name}</h2>
          {shop.description && <p className="muted" style={{ margin: 0 }}>{shop.description}</p>}
          <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14, color: 'var(--muted)' }}>
            {shop.address && <span>📍 {shop.address}</span>}
            {shop.phone && <span>📞 {shop.phone}</span>}
            {shop.shop_type !== 'RESTAURANT' && <span>⏱ Préparation : {shop.min_order_delay_minutes} min</span>}
          </div>
          {shop.shop_type === 'RESTAURANT' && (
            <Link to={`/reserver/${shop.slug}`} style={{ display: 'inline-block', marginTop: 16 }}>
              <button className="primary" style={{ padding: '12px 24px' }}>
                Réserver une table
              </button>
            </Link>
          )}
        </div>
      </div>

      {shop.shop_type === 'RESTAURANT' && products.length > 0 && (
        <p className="muted">Menu à titre informatif — les commandes en ligne ne sont pas disponibles pour ce restaurant, mais vous pouvez réserver une table ci-dessus.</p>
      )}

      {products.length === 0 ? (
        shop.shop_type === 'RESTAURANT' ? (
          <p className="muted">Consultez le menu directement au restaurant.</p>
        ) : (
          <p className="muted">Ce commerçant n’a pas encore ajouté de produits.</p>
        )
      ) : (
        categories.map(([cat, list]) => (
          <section key={cat} style={{ marginBottom: 32 }}>
            <h3>{cat}</h3>
            <div className="product-grid">
              {list.map((p) => {
                const soldOut = !p.is_made_to_order && p.stock <= 0;
                return (
                  <div key={p.id} className={`product-card${soldOut ? ' soldout' : ''}`}>
                    <div className="photo" style={{ backgroundImage: p.photo_url ? `url(${p.photo_url})` : undefined }} />
                    <div className="body">
                      <h4>{p.name}</h4>
                      {p.category && <div className="category">{p.category}</div>}
                      {p.description && <div className="description">{p.description}</div>}
                      {p.allergens.length > 0 && (
                        <div className="muted" style={{ fontSize: 11 }}>
                          Allergènes : {p.allergens.join(', ')}
                        </div>
                      )}
                      {p.is_made_to_order && <span className="badge">Sur commande</span>}
                      {p.is_perishable && <span className="badge">Produit du jour</span>}
                      <div className="price">{(p.price_cents / 100).toFixed(2)} €</div>
                      {shop.shop_type !== 'RESTAURANT' && (
                        <button className="primary" disabled={soldOut || !p.is_available} onClick={() => addToCart(p)}>
                          {soldOut ? 'Épuisé' : 'Ajouter au panier'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </main>
  );
}
